import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Inbox, ShieldAlert } from 'lucide-react';
import {
  EmptyState,
  HelpAnchor,
  SkeletonBlock,
  SkeletonCardGrid,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '@/versions/v1/chrome-oem/AskStrip';
import { useDrawer } from '@/app/providers';
import { useToast } from '@/versions/v1/shared/toast/ToastContext';
import { unpackTable } from '@/versions/v1/shared/columnar';
import { useOemData } from '@/versions/v1/shared/useOemData';
import { routeForView } from '@/versions/v1/shared/oem-routes';
import { fmtk } from '@/versions/v1/shared/oem-engine';
import type { WorkOrder } from '@/versions/v1/schema/work-order';
import { MeasureTile, SceneSection, SourceLink, TonePill } from '@/versions/v1/shared/oem-tiles';
import {
  amountLabel,
  approvalRows,
  approvalSource,
  approvalSummary,
  type ApprovalRow,
} from './ApprovalsScene.transform';

// =========================================================================
// Approvals — the demo's `renderApprovals`, source line 2109. Fork-owned.
// Route `/approvals`.
//
// This is the page the whole autonomy story rests on, and the sentence that
// makes it work is the demo's own: *nothing here has touched a machine, a
// schedule or the order book*. Sixteen agents draft; a person decides. The
// page's job is to make that boundary visible, so the copy sits at the top
// where it is read before the table, not in a footnote under it.
//
// Approving flips local state and toasts. It does not write anywhere, and the
// note under the table says so in as many words — a demo that implied a
// write-back would be lying about the one thing this page is for.
// =========================================================================

/** Domains the queue needs: drafted work orders, and the agent parts orders. */
function useApprovalsData() {
  const wos = useOemData('work-orders');
  const parts = useOemData('parts');

  const loading = wos.loading || parts.loading;
  const error = wos.error ?? parts.error ?? null;

  const ctx = useMemo(() => {
    if (!wos.data || !parts.data) return null;
    return {
      workOrders: unpackTable<WorkOrder>(wos.data.wos),
      orders: parts.data.orders,
    };
  }, [wos.data, parts.data]);

  return { ctx, loading, error };
}

/** How many queue rows the page shows before it is asked for the rest (B7). */
const QUEUE_PREVIEW_LIMIT = 6;

export function ApprovalsScene() {
  const navigate = useNavigate();
  const { open: openDrawer } = useDrawer();
  const { show } = useToast();
  const { ctx, loading, error } = useApprovalsData();

  /**
   * Which rows the user has approved, this session only.
   *
   * Local state by design: there is no write-back, and persisting it would
   * make the demo's opening state depend on whoever used the laptop last.
   */
  const [approved, setApproved] = useState<ReadonlySet<string>>(() => new Set());

  const rows = useMemo(() => (ctx ? approvalRows(ctx) : []), [ctx]);
  /**
   * B7. Twenty-four rows with an Approve button on every one reads as a work
   * queue, which is the operational persona Jay's brief excludes. Six rows
   * plus the count reads as evidence that a person still decides — the same
   * fact, in the register the room is in. Nothing is deleted; the rest is one
   * click away and the page keeps the full list once it is asked for.
   */
  const [showAllRows, setShowAllRows] = useState(false);
  const visibleRows = showAllRows ? rows : rows.slice(0, QUEUE_PREVIEW_LIMIT);
  const summary = useMemo(() => approvalSummary(rows), [rows]);
  const outstanding = rows.filter((r) => !approved.has(r.id)).length;

  const approve = useCallback(
    (row: ApprovalRow) => {
      setApproved((prev) => {
        if (prev.has(row.id)) return prev;
        const next = new Set(prev);
        next.add(row.id);
        return next;
      });
      show(row.toast);
    },
    [show],
  );

  const openSource = useCallback(
    (row: ApprovalRow) => {
      if (!ctx) return;
      const slice = approvalSource(row, ctx);
      if (!slice) return;
      openDrawer('oem-source', {
        title: slice.title,
        kicker: 'Work order',
        wide: true,
        data: slice as unknown as Record<string, unknown>,
      });
    },
    [ctx, openDrawer],
  );

  return (
    <SurfacePageLayout width="full">
      <Commentable name="ApprovalsScene">
        <SurfacePageBody className="@container max-w-[1240px] space-y-8 pt-6">
          <SurfacePageHeader
            title="Approvals"
            description="Everything the agents have drafted and are waiting on a person for — nothing here has touched a machine, a schedule or the order book."
            actions={<HelpAnchor doc="approvals/queue" />}
          />


          {error ? (
            <div
              role="alert"
              className="rounded-xl border p-4 text-sm"
              style={{
                borderColor: 'var(--hairline)',
                background: 'var(--crit-soft)',
                color: 'var(--crit)',
              }}
            >
              The approval queue could not be loaded ({error.message}). Nothing is shown
              rather than a partial list — an approvals page that hides a row is worse
              than one that is empty.
            </div>
          ) : null}

          {/* ------------------------------------------------------- */}
          {/* Summary                                                 */}
          {/* ------------------------------------------------------- */}
          <Commentable name="Summary">
            {ctx ? (
              <div className="grid grid-cols-1 gap-3 @xl:grid-cols-2 @5xl:grid-cols-4">
                <MeasureTile
                  eyebrow="Waiting on a person"
                  value={outstanding}
                  unit={
                    outstanding === summary.waiting
                      ? 'items drafted by an agent'
                      : `of ${summary.waiting} · ${summary.waiting - outstanding} approved`
                  }
                  detail={`${summary.workOrders} work orders · ${summary.partsOrders} parts requisitions · ${summary.restrictions} restriction`}
                  tone={outstanding ? 'warn' : 'good'}
                />
                <MeasureTile
                  eyebrow="Priced direct cost"
                  value={fmtk(summary.pricedTotal)}
                  unit={`across ${summary.pricedRows} work orders`}
                  detail="Labour, parts, freight and external service on the drafted orders"
                  tone="flat"
                />
                <MeasureTile
                  eyebrow="Sites affected"
                  value={summary.sites}
                  unit="mine operations"
                  detail="Each approval is written back to that site's own system of record"
                  tone="flat"
                />
                <MeasureTile
                  eyebrow="Agent autonomy"
                  value="L1"
                  unit="draft only"
                  detail="Agents propose and price; releasing to a machine or an order book stays a person's decision"
                  tone="good"
                />
              </div>
            ) : (
              <SkeletonCardGrid count={4} cols={2} />
            )}
          </Commentable>

          {/* B3: the ask chips sit under the first numbers, not above them. */}
          <AskStrip view="approvals" />

          {/* ------------------------------------------------------- */}
          {/* Queue                                                   */}
          {/* ------------------------------------------------------- */}
          <Commentable name="Queue">
            <SceneSection
              title="Drafted and waiting"
              subtitle="work orders, requisitions and one operating restriction"
              actions={
                <SourceLink onClick={() => navigate('/issues')}>
                  What raised these
                </SourceLink>
              }
            >
              {!ctx ? (
                <SkeletonBlock className="h-64 rounded-xl" />
              ) : rows.length === 0 ? (
                <EmptyState
                  icon={<Inbox className="h-8 w-8 text-zinc-400" aria-hidden="true" />}
                  title="Nothing is waiting"
                  description="No agent has drafted anything that needs a decision this morning."
                />
              ) : (
                <div
                  className="overflow-hidden rounded-xl border"
                  style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
                >
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[56rem] border-collapse text-left">
                      <caption className="sr-only">
                        Items drafted by an agent and awaiting approval. Each row can be
                        approved; approving records the decision on screen only.
                      </caption>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--hairline)' }}>
                          {['Item', 'Machine', 'Site', 'Drafted by', 'What it does'].map(
                            (h) => (
                              <th
                                key={h}
                                scope="col"
                                className="px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.08em]"
                                style={{ color: 'var(--ink-tertiary)' }}
                              >
                                {h}
                              </th>
                            ),
                          )}
                          <th
                            scope="col"
                            className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-[0.08em]"
                            style={{ color: 'var(--ink-tertiary)' }}
                          >
                            A$
                          </th>
                          <th scope="col" className="px-4 py-2.5">
                            <span className="sr-only">Approve</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleRows.map((row) => {
                          const done = approved.has(row.id);
                          return (
                            <tr
                              key={row.id}
                              style={{ borderTop: '1px solid var(--hairline)' }}
                              className={done ? 'opacity-60' : undefined}
                            >
                              <td className="px-4 py-3 align-top">
                                {row.kind === 'wo' ? (
                                  <SourceLink
                                    onClick={() => openSource(row)}
                                    className="mono !text-[12px]"
                                  >
                                    {row.id}
                                  </SourceLink>
                                ) : (
                                  <span
                                    className="mono text-[12px]"
                                    style={{ color: 'var(--ink-primary)' }}
                                  >
                                    {row.id}
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 align-top">
                                <SourceLink
                                  onClick={() => {
                                    const to = routeForView(
                                      row.machineAction.view,
                                      row.machineAction.sub,
                                    );
                                    if (to) navigate(to);
                                  }}
                                  className="mono !text-[12px]"
                                >
                                  {row.machine}
                                </SourceLink>
                              </td>
                              <td
                                className="px-4 py-3 align-top text-[12px]"
                                style={{ color: 'var(--ink-secondary)' }}
                              >
                                {row.siteName}
                              </td>
                              <td
                                className="px-4 py-3 align-top text-[12px]"
                                style={{ color: 'var(--ink-secondary)' }}
                              >
                                <span className="flex items-center gap-1.5">
                                  {row.kind === 'restrict' ? (
                                    <ShieldAlert
                                      className="h-3.5 w-3.5 shrink-0"
                                      style={{ color: 'var(--warn)' }}
                                      aria-hidden="true"
                                    />
                                  ) : null}
                                  {row.draftedBy}
                                </span>
                              </td>
                              <td
                                className="max-w-[26rem] px-4 py-3 align-top text-[12px] leading-[1.5]"
                                style={{ color: 'var(--ink-secondary)' }}
                              >
                                {row.what}
                              </td>
                              <td
                                className="mono whitespace-nowrap px-4 py-3 text-right align-top text-[12px]"
                                style={{ color: 'var(--ink-primary)' }}
                              >
                                {amountLabel(row.amount)}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-right align-top">
                                {done ? (
                                  <TonePill tone="good">
                                    <Check className="h-3 w-3" aria-hidden="true" />
                                    Approved
                                  </TonePill>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => approve(row)}
                                    aria-label={`Approve ${row.id} on ${row.machine}`}
                                    className="inline-flex h-7 items-center rounded-md px-3 text-[11.5px] font-medium transition-[background-color,transform] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)] active:translate-y-px"
                                    style={{
                                      background: 'var(--accent)',
                                      color: 'var(--surface-2)',
                                    }}
                                  >
                                    Approve
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {rows.length > QUEUE_PREVIEW_LIMIT ? (
                <button
                  type="button"
                  onClick={() => setShowAllRows((value) => !value)}
                  aria-expanded={showAllRows}
                  className="mt-3 rounded text-[11.5px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                  style={{ color: 'var(--accent)' }}
                >
                  {showAllRows
                    ? `Show the first ${QUEUE_PREVIEW_LIMIT}`
                    : `Show all ${rows.length}`}
                </button>
              ) : null}

              <p
                className="mt-3 text-[11.5px] leading-[1.6]"
                style={{ color: 'var(--ink-tertiary)' }}
              >
                Approving here updates the row on screen only. In production the approval
                is written back to SAP PM, Salesforce or the fleet management system and
                logged against the approver's identity.
              </p>
            </SceneSection>
          </Commentable>

          <p className="sr-only" role="status">
            {loading && !ctx
              ? 'Loading the approval queue.'
              : `${outstanding} items waiting on a decision.`}
          </p>
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}

export default ApprovalsScene;
